import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import Page from "components/page/Page";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Form from "components/form/Form";
import Input from "components/form/input/Input";

import { Select } from "components/select/index";
import { StatusCode } from "api/protocols";
import Toggle from "components/form/toggle/Toggle";

import { useTranslation } from "react-i18next";
import { PostCustomerType, GetCustomerType } from "api/rpc/2024-04/facilityAdmin/customer/type";
import { GetBookingCategory } from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import AutomaticDiscountSelect from "./components/AutomaticDiscountSelect";
import { IDiscount } from "api/rpc/2024-04/facilityAdmin/product/discount";
import { TCustomerType } from "redux/reducers/models/facility";
import { IBookingCategory } from "redux/reducers/models/teetime";

interface ICustomerTypeState {
  title: string;
  application: string;
  application_options: any;
  subtitle: string;
  discounts: IDiscount[];
  default: boolean;
  guest_customer_type_id: number;
  default_booking_category_id: number;
}

export default function CustomerTypesNew() {
  const history = useHistory();
  const { t } = useTranslation();
  const { Option } = Select;

  const [allCustomerTypes, setAllCustomerTypes] = useState<TCustomerType[]>([]);
  const [bookingCategories, setBookingCategories] = useState<IBookingCategory[]>([]);

  const [customerTypeState, setCustomerTypeState] = useState<ICustomerTypeState>({
    title: "",
    application: "",
    application_options: [
      {
        label: t("Green Fee"),
        value: "green_fee",
      },
      {
        label: t("Power Cart"),
        value: "power_cart",
      },
      {
        label: t("No Show"),
        value: "no_show",
      },
    ],
    subtitle: "",
    discounts: [],
    default: false,
    guest_customer_type_id: null,
    default_booking_category_id: undefined,
  });

  const selectDiscountCallback = (data: IDiscount[]) => {
    setCustomerTypeState(prev => ({ ...prev, discounts: data }));
  };

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setCustomerTypeState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleToggleChange(event: any) {
    const { id, checked } = event.target;
    setCustomerTypeState(prevState => ({ ...prevState, [id]: checked }));
  }

  function handleDropDownChange(value: any, property: string) {
    setCustomerTypeState(prevState => ({ ...prevState, [property]: value }));
  }

  function handleBookingCategoryChange(value: any) {
    setCustomerTypeState(prevState => ({
      ...prevState,
      default_booking_category_id: value,
    }));
  }

  async function loadCustomerTypes() {
    const res = await GetCustomerType(null, true);
    if (res.status !== StatusCode.OK) {
      return;
    }

    setAllCustomerTypes(res.data);
  }

  async function loadBookingCategories() {
    const res = await GetBookingCategory(null, false);
    if (res.status !== StatusCode.OK) {
      return;
    }

    setBookingCategories(res.data);
  }

  useEffect(() => {
    void loadCustomerTypes();
    void loadBookingCategories();
  }, []);

  async function saveNewCustomerType() {
    const params = {
      title: customerTypeState?.title,
      application: customerTypeState?.application,
      application_options: customerTypeState?.application_options,
      subtitle: customerTypeState?.subtitle,
      discount_ids: customerTypeState?.discounts.map(d => d.id),
      guest_customer_type_id: customerTypeState?.guest_customer_type_id,
      default: customerTypeState?.default,
      default_booking_category_id: customerTypeState?.default_booking_category_id,
    };

    const res = await PostCustomerType(params, true);
    if (res.status !== StatusCode.OK) {
      return;
    }

    history.push("/admin/settings/customer_types");
  }

  function handleGuestCustomerTypeChange(value: any) {
    if (value === 0) {
      value = null;
    }
    setCustomerTypeState(prevState => ({
      ...prevState,
      guest_customer_type_id: value,
    }));
  }

  const primaryAction = {
    content: t("Save"),
    action: saveNewCustomerType,
  };

  return (
    <Page
      title={t("New Customer Type")}
      narrow
      primaryAction={primaryAction}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Customer Types"),
          url: "/admin/settings/customer_types",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <Form>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={customerTypeState.title}
                  label={t("Title")}
                  id="title"
                  onChange={handleInputChange}
                  placeholder={t("Title")}
                />
                <Input
                  value={customerTypeState.subtitle}
                  label={t("Subtitle")}
                  id="subtitle"
                  onChange={handleInputChange}
                  placeholder={t("Subtitle")}
                />
                <Select
                  label={t("Application")}
                  className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
                  onChange={(value: any) => handleDropDownChange(value, "application")}
                >
                  {customerTypeState.application_options.map((option: any) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </FormLayout.Group>
              <FormLayout.Group alignStart>
                <Select
                  label={t("Booking Category")}
                  onChange={handleBookingCategoryChange}
                  placeholder={t("Select a booking category")}
                  helpText={t("Choose one booking category")}
                >
                  {bookingCategories.map((category: IBookingCategory, index: number) => {
                    return (
                      <Option key={index} value={category.id} name={category.title}>
                        <span>{category.title}</span>
                      </Option>
                    );
                  })}
                </Select>
                <Select
                  label={t("Guest Customer Type")}
                  onChange={handleGuestCustomerTypeChange}
                  disabled={customerTypeState.application === ""}
                  placeholder={t("Select an application first")}
                  showPlaceholder={customerTypeState.application === ""}
                  helpText={t("Automatically assigned to guests on this customer type's bookings.")}
                >
                  <Option value={0} name={t("None")}>
                    <span>{t("None")}</span>
                  </Option>
                  {allCustomerTypes.filter(type => type.application === customerTypeState.application).map((customerType: any, index: number) => {
                    return (
                      <Option key={index} value={customerType.id} name={customerType.title}>
                        <span>{customerType.title}</span>
                      </Option>
                    );
                  })}
                </Select>
              </FormLayout.Group>
              <FormLayout.Group>
                <Toggle
                  labelRight={t("Default")}
                  id="default"
                  checked={customerTypeState.default}
                  onChange={handleToggleChange}
                  labelSupport={t("This customer type will be selected by default for all new customers.")}
                />
              </FormLayout.Group>
            </FormLayout>
          </Form>
        </Card.Section>
      </Card>

      <AutomaticDiscountSelect
        onDiscountSelect={selectDiscountCallback}
        selectedDiscounts={customerTypeState.discounts}
      />
    </Page>
  );
}
